import { Button, Text } from '@/ui-kit'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import clsx from 'clsx'
import { ConnectionState } from 'livekit-client'
import { CheckCircle, ChevronDown, CircleAlert, LoaderCircle, UserPlus, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useConversationLogger } from '../../hooks/useConversationLogger'
import type { LiveKitParticipant } from '../../hooks/useLiveKit'
import { ParticipantCircles } from './ParticipantCircles'
import { RecordingCountdown } from './RecordingStartCountdown'
import { WaitingForAnotherParticipant } from './WaitingForAnotherParticipant'

type SessionLobbyProps = {
  joinedCount: number
  requiredCount: number
  readyCount: number
  isReady: boolean
  topic?: string
  instruction?: string
  onReady: () => void | Promise<void>
  onLeave: () => void | Promise<void>
  connectionState: ConnectionState
  participants: LiveKitParticipant[]
  isMuted: boolean
  toggleMute: () => void
  hasLiveKit: boolean
  recordingStartsAt: string | null
  onCountdownComplete: () => void
  inviteCode?: string
  sessionParticipants?: Array<{ participantIndex: number; isReady: boolean }>
  participantInstructions?: Array<{ participantIndex: number; instructions: string }>
  topicCollapsed: boolean
  onTopicCollapsedChange: (collapsed: boolean) => void
  instructionsCollapsed: boolean
  onInstructionsCollapsedChange: (collapsed: boolean) => void
}

export const SessionLobby = ({
  joinedCount,
  requiredCount,
  readyCount,
  isReady,
  topic,
  instruction,
  onReady,
  onLeave,
  connectionState,
  participants,
  isMuted,
  toggleMute,
  hasLiveKit,
  recordingStartsAt,
  onCountdownComplete,
  inviteCode,
  sessionParticipants = [],
  participantInstructions = [],
  topicCollapsed,
  onTopicCollapsedChange,
  instructionsCollapsed,
  onInstructionsCollapsedChange
}: SessionLobbyProps) => {
  const log = useConversationLogger()
  const [readyLoading, setReadyLoading] = useState(false)
  const [inviteOpen, setInviteOpen] = useState(false)
  const inviteLink = inviteCode
    ? `${window.location.origin}${window.location.pathname}?inviteCode=${inviteCode}`
    : window.location.href
  const [countdownSeconds, setCountdownSeconds] = useState<number | null>(null)
  const countdownCompleteRef = useRef(onCountdownComplete)
  countdownCompleteRef.current = onCountdownComplete

  useEffect(() => {
    if (!recordingStartsAt) {
      setCountdownSeconds(null)
      return
    }

    const target = new Date(recordingStartsAt).getTime()

    const tick = () => {
      const remaining = Math.ceil((target - Date.now()) / 1000)
      if (remaining <= 0) {
        setCountdownSeconds(0)
        countdownCompleteRef.current()
        return
      }
      setCountdownSeconds(remaining)
    }

    tick()
    const interval = setInterval(tick, 200)
    return () => clearInterval(interval)
  }, [recordingStartsAt])

  const isCountingDown = countdownSeconds !== null && countdownSeconds > 0

  const streamRef = useRef<MediaStream | null>(null)

  useEffect(() => {
    if (hasLiveKit) return

    let cancelled = false
    log('mic', 'Requesting microphone access (local fallback)')

    navigator.mediaDevices
      .getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      })
      .then((stream) => {
        if (cancelled) {
          for (const t of stream.getTracks()) t.stop()
          return
        }
        streamRef.current = stream
        log('mic', 'Microphone access granted (local)')
      })
      .catch((err) => {
        if (!cancelled) {
          log('error', `Microphone access denied: ${err.message}`)
          toast.error(t`Microphone access is required for voice conversations`)
        }
      })

    return () => {
      cancelled = true
      if (streamRef.current) {
        for (const t of streamRef.current.getTracks()) t.stop()
      }
    }
  }, [hasLiveKit, log])

  const handleReady = async () => {
    setReadyLoading(true)
    try {
      await onReady()
    } finally {
      setReadyLoading(false)
    }
  }

  const livekitConnected = connectionState === ConnectionState.Connected

  const micReady = hasLiveKit ? livekitConnected : !!streamRef.current
  const localParticipantIndex = participants.find((p) => p.isLocal)?.participantIndex
  const localParticipantInstruction =
    participantInstructions.find((item) => item.participantIndex === localParticipantIndex)?.instructions ?? null

  return (
    <div
      style={{ animation: 'nectar-slide-up 0.4s ease-out' }}
      className={clsx(
        'mx-auto flex w-full max-w-[800px] flex-col items-center gap-4',
        localParticipantInstruction && 'max-w-[1200px]'
      )}
    >
      <div className="max-mobile:p-4 flex w-full grow flex-col gap-4 rounded-2xl border border-solid border-gray-100 p-6">
        <button
          type="button"
          onClick={() => onTopicCollapsedChange(!topicCollapsed)}
          className="max-mobile:cursor-pointer flex w-full cursor-default items-center justify-between gap-2 text-left"
        >
          <span className="text-body-3 text-gray-90">&#8226; Topic to discuss</span>
          <ChevronDown
            className={clsx(
              'text-gray-80 max-mobile:block hidden size-4 shrink-0 transition-transform',
              topicCollapsed && '-rotate-90'
            )}
          />
        </button>
        <div className={clsx('flex flex-col gap-4', topicCollapsed && 'max-mobile:hidden')}>
          {topic && <div className="text-h5 leading-[110%] whitespace-pre-line">{topic}</div>}
          {instruction && <div className="text-gray-80 text-body2 mr-auto max-w-[800px]">{instruction}</div>}
        </div>
      </div>
      <div className="max-mobile:flex-col flex h-full w-full gap-4">
        {localParticipantInstruction && (
          <div className="text-body2 text-gray-80 max-mobile:max-w-full max-mobile:w-full mx-auto mb-4 flex max-w-[300px] flex-col gap-4 rounded-2xl border border-solid border-gray-100 p-4">
            <button
              type="button"
              onClick={() => onInstructionsCollapsedChange(!instructionsCollapsed)}
              className="max-mobile:cursor-pointer flex w-full cursor-default items-center justify-between gap-2 text-left"
            >
              <span className="text-subtitle-semibold text-gray-70 font-bold">
                <Trans> Participant Instructions</Trans>
              </span>
              <ChevronDown
                className={clsx(
                  'text-gray-80 max-mobile:block hidden size-4 shrink-0 transition-transform',
                  instructionsCollapsed && '-rotate-90'
                )}
              />
            </button>
            <div
              className={clsx('text-body2 text-gray-80', instructionsCollapsed && 'max-mobile:hidden')}
              dangerouslySetInnerHTML={{ __html: localParticipantInstruction }}
            />
          </div>
        )}

        <div className="relative flex w-full grow flex-col items-center gap-6 rounded-2xl border border-solid border-gray-100 px-8 py-10">
          {hasLiveKit ? (
            <ParticipantCircles
              participants={participants}
              isMuted={isMuted}
              toggleMute={toggleMute}
              readyCount={readyCount}
              requiredCount={requiredCount}
              sessionParticipants={sessionParticipants}
            />
          ) : (
            <WaitingForAnotherParticipant
              micReady={micReady}
              inviteLink={inviteLink}
              inviteOpen={inviteOpen}
              onInviteOpenChange={setInviteOpen}
            />
          )}

          <div className="flex w-full flex-col items-center gap-4 border-t border-solid border-gray-100 pt-3">
            {hasLiveKit ? (
              <>
                <div className="flex items-center gap-2">
                  <Text type="body2-regular" className="text-gray-90">
                    {readyCount}/{requiredCount} <Trans>ready</Trans>
                  </Text>
                </div>
                <div className="mt-4 flex flex-col items-center gap-3">
                  <div className="flex items-center gap-3">
                    {isReady ? (
                      <div className="bg-green/10 text-green flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium">
                        <CheckCircle className="size-4" />
                        <Trans>You&apos;re ready</Trans>
                      </div>
                    ) : (
                      <Button
                        size="small"
                        variant="green"
                        onClick={handleReady}
                        isDisabled={!livekitConnected || readyLoading}
                        isLoading={readyLoading}
                        className="gap-2"
                      >
                        {readyLoading ? (
                          <LoaderCircle className="size-5 animate-spin" />
                        ) : (
                          <CheckCircle className="size-5" />
                        )}
                        <Trans>I&apos;m Ready</Trans>
                      </Button>
                    )}
                    <Button size="small" variant="bordered-gray" onClick={onLeave} className="gap-2">
                      <X className="size-5" />
                      <Trans>Leave</Trans>
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <span className="bg-orange size-2.5 animate-pulse rounded-full" />
                  <Text type="body2-regular" className="text-gray-90">
                    <Trans>Waiting for participants...</Trans>
                  </Text>
                </div>
                <div className="max-mobile:flex-col flex w-full items-center justify-center gap-3">
                  {joinedCount < requiredCount && (
                    <Button
                      variant="green"
                      size="small"
                      onClick={() => setInviteOpen(true)}
                      className="mt-4 w-[190px] gap-1.5"
                    >
                      <UserPlus className="size-4" />
                      <Trans>Invite Participant</Trans>
                    </Button>
                  )}
                  {(!hasLiveKit || isReady) && (
                    <Button variant="bordered-gray" size="small" onClick={onLeave} className="mt-4 w-[190px] gap-1.5">
                      <X className="size-4" />
                      <Trans>Leave</Trans>
                    </Button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {hasLiveKit && (
        <div className="flex w-full items-start gap-2 rounded-2xl border border-solid border-gray-100 px-7 py-4">
          <CircleAlert className="text-gray-90 mt-1 size-5 shrink-0" />
          <div className="text-gray-90 text-body-2 flex flex-col gap-1">
            <div>
              <Trans>
                Before recording begins, all participants should review the topic and ensure they understand the
                discussion objectives and their assigned roles.
              </Trans>
            </div>
            <div>
              <Trans>
                {' '}
                Once everyone is aligned and ready to proceed, click the &quot;I&apos;m Ready&quot; button.
              </Trans>
            </div>
            <div>
              <Trans>Recording will begin after a short countdown once all participants are ready.</Trans>
            </div>
          </div>
        </div>
      )}

      {isCountingDown && (
        <div className="absolute inset-0 z-20 flex items-center justify-center rounded-2xl bg-black/40 backdrop-blur-[1px]">
          <div className="bg-background flex flex-col items-center gap-4 rounded-2xl border border-solid border-gray-100 px-8 py-6 shadow-lg">
            <RecordingCountdown countdownSeconds={countdownSeconds} />
            <Text type="body3-regular" className="text-gray-80 text-center">
              <Trans>Get ready to start your conversation!</Trans>
            </Text>
          </div>
        </div>
      )}
    </div>
  )
}
